import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, UserCircle2 } from "lucide-react";
import { setAuthToken, type LoginResponse } from "@/lib/api";

type LoginError = { error?: string };

/**
 * Standalone Bridge login. Posts username + password to
 * /api/v1/auth/login (LocalUserService bcrypt verify) and stores the
 * returned HS256 JWT for the axios interceptor in lib/api.ts to attach
 * as a Bearer header. Default seed is admin/admin — operators replace
 * via datumcraft.auth.users[] in application.yml.
 *
 * <p>Mirrors operate-ui's LoginPage so a single ops playbook covers
 * both products. Production deployments expose neither this page nor
 * /api/v1/auth — the operator's IdP handles auth.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (resp.status === 401) {
        const body = (await resp.json().catch(() => ({}))) as LoginError;
        throw new Error(body.error ?? "Invalid credentials");
      }
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as LoginError;
        throw new Error(body.error ?? `Login failed (HTTP ${resp.status})`);
      }
      const data = (await resp.json()) as LoginResponse;
      setAuthToken(data.token);
      navigate("/parse", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-57px)] max-w-md items-center justify-center px-6">
      <form
        onSubmit={onSubmit}
        className="w-full space-y-5 rounded-xl border border-bridge-slate/25 bg-white p-7 shadow-lg"
      >
        <div>
          <h1 className="font-display text-xl font-semibold text-bridge-ink">Sign in</h1>
          <p className="mt-1 text-xs text-bridge-slate">
            DatumCraft Bridge — enter your administrator credentials.
          </p>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-bridge-ink">Username</span>
          <div className="flex items-center gap-2 rounded-md border border-bridge-slate/40 px-3 py-2 focus-within:border-bridge-primary focus-within:ring-1 focus-within:ring-bridge-primary/30">
            <UserCircle2 size={14} className="text-bridge-slate" />
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-transparent text-sm outline-none"
              autoFocus
              autoComplete="username"
            />
          </div>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-bridge-ink">Password</span>
          <div className="flex items-center gap-2 rounded-md border border-bridge-slate/40 px-3 py-2 focus-within:border-bridge-primary focus-within:ring-1 focus-within:ring-bridge-primary/30">
            <KeyRound size={14} className="text-bridge-slate" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent text-sm outline-none"
              autoComplete="current-password"
            />
          </div>
        </label>

        {error && (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !username.trim() || !password}
          className="w-full rounded-md bg-bridge-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-bridge-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <p className="text-center text-2xs text-bridge-slate">
          First-time login? The default admin is <code className="font-mono">admin</code> /{" "}
          <code className="font-mono">admin</code>. Replace via{" "}
          <code className="font-mono">datumcraft.auth.users[]</code>.
        </p>
      </form>
    </div>
  );
}
